import React, { useMemo } from 'react';

import { Checkbox } from 'antd';

import { isJobEvent } from '@libs/EventTypes';
import { showDateNoTime, showPeriodNoTime } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import EventTypeTag from '@components/EventDetails/EventTypeTag';
import CustomSwitch from '@components/Gui/CustomSwitch';
import CustomSelect from '@components/Gui/NwForm/CustomSelect';

import { isPastEvent, isPastEventDate, useSendProjectDrawer } from './SendProjectDrawerContext';

const getEventLabel = (event) => event.Name || event.Title || event.Service?.Name || 'Untitled event';

/**
 * Models of a work date: the total, with the confirmed ones (job events only) and the
 * cancelled ones in brackets.
 */
const getWorkDateCounter = (event, eventDate) => {
    const models = eventDate.EventDatesModels;
    const details = [];

    if (isJobEvent(event.TypeName)) {
        const confirmed = models.filter((model) => !model.Cancel && model.OptionNumber === 10).length;
        details.push(`${confirmed} confirmed`);
    }

    const cancelled = models.filter((model) => model.Cancel).length;
    if (cancelled > 0) {
        details.push(`${cancelled} cancelled`);
    }

    return details.length > 0 ? `${models.length} (${details.join(', ')})` : `${models.length}`;
};

/**
 * A whole event is past only when none of its dates is still to come
 */
const isPastProjectEvent = (event) =>
    event.elType === 'Event' ? event.EventDates.every(isPastEventDate) : isPastEvent(event);

/**
 * ScopeFilters is the left rail of the drawer: it always lists the whole project and
 * decides what the selection list on the right can contain.
 */
const ScopeFilters = () => {
    const { state, actions } = useSendProjectDrawer();
    const { projectEventsList, scope, selectedModel, availableModels, hideCancelledModels, hidePastDates } = state;

    /**
     * Model options for the filter, alphabetically sorted
     */
    const modelOptions = useMemo(() => {
        const options = availableModels.map((modelID) => ({
            value: modelID,
            label: getContactName(modelID),
        }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        options.unshift({ value: '', label: 'all models' });
        return options;
    }, [availableModels]);

    /**
     * Past events are grouped at the bottom, out of the way of the current ones
     */
    const [upcomingEvents, pastEvents] = useMemo(() => {
        const upcoming = [];
        const past = [];
        projectEventsList.forEach((event) => (isPastProjectEvent(event) ? past : upcoming).push(event));
        return [upcoming, past];
    }, [projectEventsList]);

    /**
     * A booking event follows its dates: checked when all of them are, partial when some are
     */
    const getEventCheckState = (event) => {
        if (event.elType !== 'Event') {
            return { checked: scope.NonBookingEvents.includes(event.uniqID), indeterminate: false };
        }
        const checkedDates = event.EventDates.filter((eventDate) => scope.EventDates.includes(eventDate.ID)).length;
        return {
            checked: checkedDates === event.EventDates.length,
            indeterminate: checkedDates > 0 && checkedDates < event.EventDates.length,
        };
    };

    const renderEventNode = (event) => {
        const { checked, indeterminate } = getEventCheckState(event);

        return (
            <div className="scope-node-group" key={event.uniqID}>
                <div className="scope-node">
                    <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={(e) => actions.toggleEvent(event, e.target.checked)}
                    />
                    <EventTypeTag event={event} type={event.Type || event.TypeName} />
                    <span className="scope-node-label">{getEventLabel(event)}</span>
                    {event.elType !== 'Event' && (
                        <span className="scope-node-info">
                            {showPeriodNoTime(event.CommonStartDate, event.CommonEndDate)}
                        </span>
                    )}
                </div>
                {event.elType === 'Event' &&
                    event.EventDates.map((eventDate) => (
                        <div className="scope-node scope-node-date" key={eventDate.ID}>
                            <Checkbox
                                checked={scope.EventDates.includes(eventDate.ID)}
                                onChange={(e) => actions.toggleEventDate(eventDate.ID, e.target.checked)}
                            />
                            <span className="scope-node-label">{showDateNoTime(eventDate.FromDate)}</span>
                            <span className="scope-node-info">{getWorkDateCounter(event, eventDate)}</span>
                        </div>
                    ))}
            </div>
        );
    };

    return (
        <div className="scope-filters">
            <div className="scope-filters-header">
                <p className="scope-filters-title">Filters</p>
                <CustomSwitch value={hidePastDates} onChange={actions.setHidePastDates} text="Hide past dates" />
                <CustomSwitch
                    value={hideCancelledModels}
                    onChange={actions.setHideCancelledModels}
                    text="Hide cancelled models"
                />
                <CustomSelect
                    options={modelOptions}
                    value={selectedModel || ''}
                    onChange={(value) => actions.setSelectedModel(value === '' ? null : value)}
                    placeholder="Select model"
                    allowClear={true}
                />
            </div>
            <div className="scope-filters-tree">
                <p className="scope-filters-separator">events &amp; dates</p>
                {upcomingEvents.map(renderEventNode)}
                {pastEvents.length > 0 && <p className="scope-filters-separator">past</p>}
                {pastEvents.map(renderEventNode)}
            </div>
        </div>
    );
};

export default ScopeFilters;
